import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { trackPurchase } from '@/lib/analytics';

/**
 * Página de sucesso após pagamento de anúncio no Marketplace.
 * Faz polling da sessão Stripe até confirmar `paid`.
 */
export default function MarketplacePagamentoSucesso() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('loading'); // loading | paid | pending | failed
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      if (cancelled) return;
      count += 1;
      setAttempts(count);
      try {
        const res = await api.get(`/marketplace/checkout/status/${sessionId}`);
        setInfo(res.data);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          // Conversão de compra — confirmada pelo backend, disparada 1x por sessão.
          try {
            const key = `mp_purchase_fired_${sessionId}`;
            if (!localStorage.getItem(key)) {
              trackPurchase({
                transactionId: sessionId,
                value: (res.data.amount_total || 0) / 100,
                currency: (res.data.currency || 'EUR').toUpperCase(),
              });
              localStorage.setItem(key, '1');
            }
          } catch (_) {}
          return;
        }
        if (count >= 12) {
          // ~60s timeout
          setStatus('pending');
          return;
        }
        setTimeout(poll, 5000);
      } catch (err) {
        setStatus('failed');
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-slate-100 flex items-center justify-center p-6"
      data-testid="marketplace-payment-success-page"
    >
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-emerald-600 animate-spin" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">A confirmar pagamento…</h1>
            <p className="mt-2 text-sm text-slate-600">
              Estamos a verificar com o Stripe. Tentativa {attempts}/12. Não feche esta janela.
            </p>
          </>
        )}

        {status === 'paid' && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Pagamento confirmado!</h1>
            <p className="mt-2 text-sm text-slate-700">
              O seu anúncio foi recebido e está agora <strong>pendente de validação</strong> pelo nosso
              administrador. Vai receber um email assim que for aprovado e ficar online.
            </p>
            {info?.amount_total ? (
              <p className="mt-2 text-xs text-slate-500">
                Valor pago: {(info.amount_total / 100).toFixed(2)} {(info.currency || 'EUR').toUpperCase()}
              </p>
            ) : null}
            <Link
              to="/"
              className="mt-6 inline-block rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              data-testid="marketplace-payment-success-back"
            >
              Voltar ao Marketplace
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <Clock className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Pagamento em processamento</h1>
            <p className="mt-2 text-sm text-slate-700">
              Para pagamentos por <strong>Multibanco</strong>, pode demorar alguns minutos a horas
              até confirmarmos. Receberá um email com a confirmação assim que o pagamento entrar.
            </p>
            <Link
              to="/"
              className="mt-6 inline-block rounded-xl border border-slate-300 px-5 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Voltar ao Marketplace
            </Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-500" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Não conseguimos confirmar</h1>
            <p className="mt-2 text-sm text-slate-700">
              Houve um problema a verificar o estado do pagamento. Se o valor foi debitado,
              contacte-nos com o número da sessão: <code className="font-mono text-xs">{sessionId}</code>
            </p>
            <Link
              to="/"
              className="mt-6 inline-block rounded-xl border border-slate-300 px-5 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Voltar ao Marketplace
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
